import { Label } from "layout/typography/Label";
import { BENEFIT_LABELS } from "pages/HospitalsPage/components/BenefitSelection/constants";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { useMemo, useState } from "react";
import { useGetBenefitFallbacksQuery } from "store/queries/benefitFallbacks";
import styled from "styled-components";
import { IBenefitFallbackRow } from "types/BenefitFallbacks/BenefitFallbacks";

import {
  appliesToInWords,
  benefitLabel,
  borrows,
  ladderInWords,
  readsFromInWords,
  tierInWords,
} from "./fallbacksWording";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
  }
`;

/** The filters' "every" choice. A Dropdown cannot hold null as a distinct option value. */
const EVERY = "__every__";

export const FALLBACKS_INTRO =
  "For each hospital, which service line each benefit is read from, and what the estimate falls back to when the payer's answer for that line is missing. Read only — a hospital's own rows are changed on its benefit selection screen.";

const rowKey = (row: IBenefitFallbackRow, index: number): string =>
  `${row.accountId}-${row.benefit}-${row.serviceScope ?? "all"}-${
    row.payerClass ?? "any"
  }-${index}`;

/**
 * CLE-3013 — Eligibility › Fallbacks: one read-only table of every hospital's live benefit map rows
 * (`GET /eligibility/benefit-fallbacks`). Nothing like it was visible anywhere: the SUBSTITUTE rung
 * (ASC ↔ Hospital outpatient, and since CLE-3013 MRI/CT → Diagnostic X-Ray) was set by the seeder and
 * shown only on an estimate that used it. Filter by hospital and by benefit. No create, edit or
 * delete. SuperAdmin like every tab on this page; the API is the boundary.
 */
export function FallbacksTab() {
  const { data, isLoading, isError } = useGetBenefitFallbacksQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [hospital, setHospital] = useState<string>(EVERY);
  const [benefit, setBenefit] = useState<string>(EVERY);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const hospitalOptions = useMemo(() => {
    const seen = new Map<string, string>();
    rows.forEach((row) => seen.set(row.accountId, row.accountName));
    return [
      { label: "Every hospital", value: EVERY },
      ...[...seen.entries()].map(([value, label]) => ({ label, value })),
    ];
  }, [rows]);
  const benefitOptions = [
    { label: "Every benefit", value: EVERY },
    ...Object.entries(BENEFIT_LABELS).map(([value, label]) => ({
      label,
      value,
    })),
  ];

  const shown = rows.filter(
    (row) =>
      (hospital === EVERY || row.accountId === hospital) &&
      (benefit === EVERY || row.benefit === benefit)
  );

  const readsFromBody = (row: IBenefitFallbackRow) => {
    const { reads, legacyAlso } = readsFromInWords(row);
    return (
      <div className="flex flex-column gap-1">
        <span>{reads}</span>
        {legacyAlso && (
          <small className="text-color-secondary">
            Legacy also falls back to: {legacyAlso}
          </small>
        )}
      </div>
    );
  };

  const ifMissingBody = (row: IBenefitFallbackRow) => (
    <span className={borrows(row) ? "font-semibold" : undefined}>
      {ladderInWords(row)}
    </span>
  );

  return (
    <div className="flex flex-column gap-3" data-testid="fallbacksTab">
      <Surface>
        <p className="mt-0 mb-0 text-color-secondary">{FALLBACKS_INTRO}</p>
      </Surface>

      <Surface>
        <div className="flex flex-wrap gap-3 mb-2 align-items-end">
          <div className="flex flex-column gap-1">
            <Label htmlFor="fallbacks.hospital">Hospital</Label>
            <Dropdown
              inputId="fallbacks.hospital"
              data-testid="fallbacks.hospital"
              options={hospitalOptions}
              value={hospital}
              onChange={(event) => setHospital(event.value || EVERY)}
              filter
              style={{ minWidth: "16rem" }}
            />
          </div>
          <div className="flex flex-column gap-1">
            <Label htmlFor="fallbacks.benefit">Benefit</Label>
            <Dropdown
              inputId="fallbacks.benefit"
              data-testid="fallbacks.benefit"
              options={benefitOptions}
              value={benefit}
              onChange={(event) => setBenefit(event.value || EVERY)}
              style={{ minWidth: "14rem" }}
            />
          </div>
        </div>

        {isLoading && (
          <div className="flex justify-content-center p-3">
            <ProgressSpinner
              style={{ width: "2rem", height: "2rem" }}
              data-testid="fallbacks.loading"
            />
          </div>
        )}
        {isError && (
          <Message
            severity="error"
            text="The fallbacks could not be loaded."
            data-testid="fallbacks.error"
          />
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <Message
            severity="info"
            text="No hospital has a benefit map yet."
            data-testid="fallbacks.empty"
          />
        )}
        {!isLoading && !isError && rows.length > 0 && (
          <StyledDataTable
            value={shown.map((row, index) => ({
              ...row,
              key: rowKey(row, index),
            }))}
            dataKey="key"
            className="w-full"
            data-testid="fallbacks.table"
            emptyMessage="No rows match these filters."
          >
            <Column
              header="Hospital"
              field="accountName"
              style={{ width: "14rem" }}
            />
            <Column
              header="Benefit"
              body={(row: IBenefitFallbackRow) => benefitLabel(row)}
              style={{ width: "11rem" }}
            />
            <Column
              header="Applies to"
              body={(row: IBenefitFallbackRow) => appliesToInWords(row)}
            />
            <Column header="Reads from" body={readsFromBody} />
            <Column header="If missing" body={ifMissingBody} />
            <Column
              header="Tier"
              body={(row: IBenefitFallbackRow) => tierInWords(row)}
              style={{ width: "9rem" }}
            />
          </StyledDataTable>
        )}
      </Surface>
    </div>
  );
}
